import { useEffect, useRef, useState } from 'react';
import { Sparkles, FileText, AlertCircle, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import type { VideoTranscript, VideoTranscriptWord } from '@/types';

export interface VideoWithCaptionsProps {
  videoUrl: string;
  transcript?: VideoTranscript;
  className?: string;
}

/**
 * Native <video> player with optional WebVTT captions track and a collapsible
 * searchable transcript panel. Clicking a transcript word seeks the video.
 *
 * Behavior:
 *  - status 'pending'   → small "Generating captions…" badge under the player
 *  - status 'completed' → CC track auto-attached (off by default), transcript panel toggleable
 *  - status 'failed'    → muted error badge, no panel
 *  - status 'disabled'  → no badge, no panel (silent — feature is off)
 *  - status undefined   → behaves like a plain video player
 */
export function VideoWithCaptions({ videoUrl, transcript, className }: VideoWithCaptionsProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [showTranscript, setShowTranscript] = useState(false);
  const [query, setQuery] = useState('');
  const [currentTime, setCurrentTime] = useState(0);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onTime = () => setCurrentTime(v.currentTime);
    v.addEventListener('timeupdate', onTime);
    return () => v.removeEventListener('timeupdate', onTime);
  }, []);

  const status = transcript?.status;
  const words = transcript?.words ?? [];
  const text = transcript?.text ?? '';

  const filteredWords = query.trim()
    ? words.filter((w) => w.word.toLowerCase().includes(query.toLowerCase()))
    : words;

  function seekTo(sec: number) {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = Math.max(0, sec);
    v.play().catch(() => {});
  }

  return (
    <div className={cn('space-y-2', className)}>
      <div className="rounded-[var(--radius-md)] overflow-hidden bg-black border border-[var(--color-border)]">
        <video
          ref={videoRef}
          src={videoUrl}
          controls
          playsInline
          preload="metadata"
          className="w-full max-h-[480px] block"
          crossOrigin={transcript?.vttUrl ? 'anonymous' : undefined}
        >
          {transcript?.vttUrl && status === 'completed' && (
            <track
              kind="captions"
              src={transcript.vttUrl}
              srcLang={transcript.language ?? 'en'}
              label="English (auto)"
              default={false}
            />
          )}
        </video>
      </div>

      {/* Status badge */}
      {status === 'pending' && (
        <div className="flex items-center gap-2 text-xs text-[var(--color-text-secondary)]">
          <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" />
          <span>Generating captions and transcript…</span>
        </div>
      )}
      {status === 'failed' && (
        <div className="flex items-center gap-2 text-xs text-[var(--color-warning)]">
          <AlertCircle className="w-3.5 h-3.5" aria-hidden="true" />
          <span>Captions unavailable for this video.</span>
        </div>
      )}

      {/* Transcript panel toggle (only when we actually have one) */}
      {status === 'completed' && (text || words.length > 0) && (
        <div className="space-y-2">
          <button
            type="button"
            onClick={() => setShowTranscript((s) => !s)}
            aria-expanded={showTranscript}
            className="inline-flex items-center gap-2 text-xs font-medium text-[var(--color-accent-secondary)] hover:underline"
          >
            <FileText className="w-3.5 h-3.5" aria-hidden="true" />
            {showTranscript ? 'Hide transcript' : 'Show transcript'}
            <Sparkles className="w-3 h-3 opacity-60" aria-hidden="true" />
          </button>

          {showTranscript && (
            <div className="rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-bg-secondary)] p-3 space-y-2">
              {words.length > 0 && (
                <input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search transcript…"
                  className="w-full px-2 py-1 text-sm rounded-[var(--radius-sm)] bg-[var(--color-bg-tertiary)] border border-[var(--color-border)] text-[var(--color-text-primary)] focus:outline-none focus:border-[var(--color-accent-primary)]"
                />
              )}
              <div className="max-h-64 overflow-y-auto text-sm leading-relaxed text-[var(--color-text-primary)]">
                {words.length > 0 ? (
                  <WordsView
                    words={filteredWords}
                    currentTime={currentTime}
                    onSeek={seekTo}
                  />
                ) : (
                  <p className="whitespace-pre-wrap">{text}</p>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function WordsView({
  words,
  currentTime,
  onSeek,
}: {
  words: VideoTranscriptWord[];
  currentTime: number;
  onSeek: (sec: number) => void;
}) {
  return (
    <p className="flex flex-wrap gap-x-1 gap-y-0.5">
      {words.map((w, i) => {
        const active = currentTime >= w.startSec && currentTime <= w.endSec;
        return (
          <button
            key={i}
            type="button"
            onClick={() => onSeek(w.startSec)}
            className={cn(
              'rounded px-0.5 transition-colors',
              active
                ? 'bg-[var(--color-accent-primary)] text-white'
                : 'hover:bg-[var(--color-bg-tertiary)]',
            )}
            title={`Jump to ${w.startSec.toFixed(1)}s`}
          >
            {w.word}
          </button>
        );
      })}
    </p>
  );
}
